import { FC, useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom"
import { Box } from "@mui/material"
import { AuthnTransaction, OAuthError } from "@okta/okta-auth-js"
import { useOktaAuth } from "@okta/okta-react"

import {
  FormInput,
  LinkText,
  UButton,
  ULoading,
  UText,
} from "../../../components"
import { useAppDispatch } from "../../../core/app/hooks"
import { setAlert } from "../../../core/app/slices/alert/alertSlice"
import { validations } from "../../../core/utils/validations"
import { CENETER_COLUMN_ALIGN } from "../../../theme/theme.util"
import ErrorAlert from "../components/ErrorAlert"

import PasswordIcon from "./PasswordIcon"
import { getWebuid } from "@/core/config/okta.config"

interface IUser {
  username: string
  password: string
}

const Login: FC = () => {
  const { t } = useTranslation("common")
  const { oktaAuth } = useOktaAuth()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const redirectTo = searchParams.get("redirectTo")

  const [visibilePassword, setVisibilePassword] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>()
  const [isLoginLoading, setIsLoginLoading] = useState(false)
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<IUser>({ mode: "onSubmit" })

  useEffect(() => {
    setIsLoginLoading(true)
    const checkSessions = async () => {
      try {
        const isAuthenticated = await oktaAuth.isAuthenticated()
        if (isAuthenticated) {
          getWebuid() // Fetch web-uuid if authenticated
          setIsLoginLoading(false)
          return
        }
        const sessionExists = await oktaAuth.session.exists()
        if (sessionExists) {
          // Redirect only if not already authenticated
          await oktaAuth.signInWithRedirect({ originalUri: redirectTo })
        } else {
          setIsLoginLoading(false)
        }
      } catch (error) {
        console.error("Error checking session:", error)
        setIsLoginLoading(false)
      }
    }
    checkSessions()
    //unmount
    return () => {
      setIsLoginLoading(false)
    }
  }, [oktaAuth]) 

  const onSubmit: SubmitHandler<IUser> = async (data) => {
    setIsLoginLoading(true)
    setErrorMsg(null)
    await oktaAuth
      .signInWithCredentials(data)
      .then((transaction: AuthnTransaction) => {
        if (transaction.status === "SUCCESS") {
          setIsLoginLoading(false)
          getWebuid()
          oktaAuth.signInWithRedirect({
            sessionToken: transaction.sessionToken,
            originalUri: redirectTo,
          })
        } else if (transaction.status === "PASSWORD_EXPIRED") {
          // Password has expired - redirect to change password page
          setIsLoginLoading(false)
          const stateToken = transaction.data?.stateToken ?? transaction.stateToken
          if (stateToken) {
            navigate(`/auth/changePassword?stateToken=${encodeURIComponent(stateToken)}`)
          } else {
            setErrorMsg("PASSWORD_EXPIRED_NO_TOKEN")
          }
        } else if (transaction.status === "PASSWORD_WARN") {
          // Password is about to expire - allow login but show warning
          setIsLoginLoading(false)
          getWebuid()
          // Show warning snackbar about password expiry
          dispatch(
            setAlert({
              message: t("auth.passwordWarnMessage"),
              severity: "warning",
            }),
          )
          oktaAuth.signInWithRedirect({
            sessionToken: transaction.sessionToken,
            originalUri: redirectTo,
          })
        } else {
          throw new Error(`Could not sign in: ${transaction.status}`)
        }
      })
      .catch((e: OAuthError) => {
        setIsLoginLoading(false)
        if (e.errorCode === "E0000004") {
          setErrorMsg("E0000004")
        } else if (e.errorCode === "E0000084" || e.errorCode === "E0000085") {
          // Okta error codes for password expiry
          setErrorMsg("PASSWORD_EXPIRED")
        } else {
          setErrorMsg("UNKNOWN_ERROR")
        }
      })
  }

  return (
    <>
      <ULoading isLoading={isLoginLoading} />
      <UText variant={"h4"} color={"text"} sxProp={{ textAlign: "center" }}>
        {t("auth.logintitle")}
      </UText>
      {errorMsg && (
        <ErrorAlert
          message={
            errorMsg === "PASSWORD_EXPIRED" || errorMsg === "PASSWORD_EXPIRED_NO_TOKEN"
              ? t("auth.changePassword.passwordExpiredMessage")
              : errorMsg === "UNKNOWN_ERROR"
              ? t("auth.authenticationError")
              : t(`auth.oautherror.${errorMsg}`, errorMsg)
          }
        />
      )}
      <Box
        component={"form"}
        data-testid={"login-form"}
        sx={{ ...CENETER_COLUMN_ALIGN, width: "100%", mt: 1 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormInput
          inputLabel={"formfields.email"}
          fieldName={"username"}
          rules={{
            required: "formfieldErrors.emailrequired",
            pattern: {
              value: validations.email.pattern.value,
              message: "formfieldErrors.userEmailinvalid",
            },
          }}
          errors={errors}
          control={control}
        />
        <Box component={"div"} sx={{ alignSelf: "start", mt: -1, mb: 2 }}>
          <LinkText
            to={"/auth/forgotUsername"}
            linkText={t("auth.forgotemailLinkText")}
            component={RouterLink}
            sxProp={{
              "&:hover": { textDecoration: "none" },
            }}
          />
        </Box>
        <FormInput
          inputLabel={"formfields.password"}
          fieldName={"password"}
          fieldType={visibilePassword ? "text" : "password"}
          rules={{ required: "formfieldErrors.passwordrequired" }}
          errors={errors}
          control={control}
          endAdornmentIcon={
            <PasswordIcon
              updatePasswordVisible={setVisibilePassword}
              isError={!!(errors && errors.password)}
            />
          }
        />
        <Box component={"div"} sx={{ alignSelf: "start", mt: -1, mb: 2 }}>
          <LinkText
            to={"/auth/forgotPassword"}
            linkText={t("auth.forgotpasswordLinkText")}
            component={RouterLink}
            sxProp={{
              "&:hover": { textDecoration: "none" },
            }}
          />
        </Box>
        <UButton
          sxProp={{ mt: 3, width: "100%" }}
          variant={"contained"}
          btnType={"submit"}
          btnText={t("button.login")}
        />
      </Box>
    </>
  )
}

export default Login
